import './chat-content.css'
import FaceIcon from '../images/face-icon.png'
import DocumentSearch from '../images/doc-search.png'
import Stacks from '../images/stacks.png'
import Enter from '../images/enter.png'
import Mic from '../images/mic.png'
import PdfIcon from '../images/pdf-icon.png'
import DocIcon from '../images/doc-icon.png'
import { useRef, useEffect, useState } from 'react'
import { postData } from '../utils/apis/api'
import { Toast, ToastContainer, Modal, Button } from 'react-bootstrap'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm' // For tables, task lists, etc.
import rehypeRaw from 'rehype-raw' // To allow raw HTML within markdown (optional)
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter' // Syntax highlighting component
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism' // Code block theme
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react'

import 'react-loading-skeleton/dist/skeleton.css'
import PolicyDialog from '../components/policy'

const ChatContent = ({ chatContentState }) => {
  const [token, setToken] = useState(null)
  const [isAudioRoom, setIsAudioRoom] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [showTooltip, setShowTooltip] = useState(false)
  const [chatHistory, setChatHistory] = useState([])
  const [showChat, setShowChat] = useState(true)
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false) // New loading state
  const [preview, setPreview] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false) // Track if user scrolled up
  const chatRef = useRef(null) // Ref for the chat window
  const textareaRef = useRef(null)
  const tooltipRef = useRef(null) // Reference to the tooltip
  const [isUploading, setIsUploading] = useState(false) // Spinner state
  const [audioFile, setAudioFile] = useState(null)
  const [showMicModal, setShowMicModal] = useState(false)
  const [modalTitle, setModalTitle] = useState('Recording...')

  // Audio recording state and refs
  const [isRecording, setIsRecording] = useState(false)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const [audioUrl, setAudioUrl] = useState(null)
  const audioRef = useRef(null)
  const [imageSrc, setImageSrc] = useState(null)

  const handleShowToast = (message) => {
    setToastMessage(message)
    setShowToast(true)
    setTimeout(() => {
      setShowToast(false)
    }, 3000)
  }

  function determineLiveKitServerUrl() {
    const url = window.location.href
    let serverUrl = ''

    if (url.includes('localhost')) {
      serverUrl = 'wss://tranquilla-voice-dev-8ss7lzoz.livekit.cloud'
    } else if (url.includes('tranquilla.nearlyhuman.ai')) {
      serverUrl = 'wss://tranquilla-c1gx9h6q.livekit.cloud'
    } else if (url.includes('tranquilla.mresult.com')) {
      serverUrl = 'wss://tranquilla-voice-dev-8ss7lzoz.livekit.cloud'
    } else if (url.includes('tranquilla-dev.mresult.com')) {
      serverUrl = 'wss://token-server-voice-xl4kpdnf.livekit.cloud'
    } else if (url.includes('tranquilla-dev.nearlyhuman.ai/')) {
      serverUrl = 'wss://tranquilla-dev-xlg7p3hk.livekit.cloud'
    }
    console.log('serverUrl', serverUrl)

    return serverUrl
  }

  useEffect(() => {
    console.log('isRecording', isRecording)
  }, [isRecording])

  useEffect(() => {
    const fetchToken = async () => {
      const userId = sessionStorage.getItem('user_id') || 'guest_' + Date.now()
      const roomName = userId + new Date().getDate()

      try {
        // Determine the API domain based on the current URL
        const url = window.location.href
        let apiDomain = ''

        if (url.includes('localhost')) {
          apiDomain = 'https://tranquilla-ai-dev.mresult.com/'
        } else if (url.includes('tranquilla.nearlyhuman.ai')) {
          apiDomain = 'https://tranquilla-ai.nearlyhuman.ai/'
        } else if (url.includes('tranquilla.mresult.com')) {
          apiDomain = 'https://tranquilla-ai.mresult.com/'
        } else if (url.includes('tranquilla-dev.mresult.com')) {
          apiDomain = 'https://tranquilla-ai-dev.mresult.com/'
        } else if (url.includes('tranquilla-dev.nearlyhuman.ai')) {
          apiDomain = 'https://tranquilla-ai-dev.nearlyhuman.ai/'
        }

        const res = await fetch(
          `${apiDomain}get_token?room_name=${sessionStorage.getItem('user_id') + new Date()
          }&participant_name=${sessionStorage.getItem('user_id')}`
        )

        const data = await res.json()

        if (data) {
          setToken(data)
          setIsAudioRoom(true)
          console.log('LiveKit token fetched')
        } else {
          console.error('Invalid token response:', data)
        }
      } catch (error) {
        console.error('Error fetching token:', error)
      }
    }

    if (showMicModal) {
      fetchToken()
    } else {
      // When mic modal closes, disconnect LiveKit
      setIsAudioRoom(false)
      setToken(null)
    }
  }, [showMicModal])

  useEffect(() => {
    const fetchIcon = async () => {
      // Check if userType is not 'admin' (meaning it is 'user')
      if (sessionStorage.getItem('userType') !== 'admin') {
        try {
          const data = {
            agent_name: sessionStorage.getItem('agentName'),
            type_: 'icon',
            filename: `${sessionStorage.getItem('agentName')}.png`,
          }

          // Make the API call to fetch the icon
          const response = await postData('/fetch-icon', data)

          if (response) {
            // Prefix the Base64 data with the appropriate MIME type
            setImageSrc(`data:image/png;base64,${response}`)
          } else {
            // Fallback if no icon is returned
            setImageSrc(FaceIcon) // Set the default icon
          }
        } catch (error) {
          console.error('Error fetching user icon:', error)
          setImageSrc(FaceIcon) // Fallback to the default icon
        }
      }
    }

    // Call the fetchIcon function
    fetchIcon()
  }, [])

  // Function to handle file uploads and update chat history
  // const handleFileChange = async (event) => {
  //   const selectedFile = event.target.files[0]
  //   setFile(selectedFile)

  //   const fileReader = new FileReader()
  //   fileReader.onloadend = async () => {
  //     setPreview(fileReader.result)

  //     if (selectedFile) {
  //       const fileURL = URL.createObjectURL(selectedFile)
  //       const formData = new FormData()
  //       formData.append('file', selectedFile)

  //       setIsUploading(true)

  //       // Add the uploaded file to chat history as a new entry with a local file URL
  //       setChatHistory((prevChatHistory) => [
  //         ...prevChatHistory,
  //         {
  //           query: `Uploaded File: ${selectedFile.name}`,
  //           response: '',
  //           fileURL: fileURL, // Local URL for uploaded file
  //           type: 'uploaded-audio',
  //           fileName: selectedFile.name,
  //         },
  //       ])

  //       try {
  //         const response = await postData('upload', formData, {
  //           headers: {
  //             Accept: 'application/json',
  //             'Content-Type': 'multipart/form-data',
  //           },
  //         })

  //         // Update chat history with the server's response (which could be a processed audio file)
  //         setChatHistory((prevChatHistory) =>
  //           prevChatHistory.map((chat) =>
  //             chat.fileName === selectedFile.name
  //               ? { ...chat, response: response.data.fileURL || response.data } // Update with response file URL or data
  //               : chat
  //           )
  //         )

  //         handleShowToast('File uploaded successfully')
  //         setShowTooltip(false)
  //         setFile(null)
  //         setPreview('')
  //       } catch (error) {
  //         console.error('Error uploading file:', error)
  //         handleShowToast('Error uploading file')

  //         // Update the chat history with the error message
  //         setChatHistory((prevChatHistory) =>
  //           prevChatHistory.map((chat) =>
  //             chat.fileName === selectedFile.name
  //               ? {
  //                 ...chat,
  //                 response: 'Error processing file. Please try again.',
  //               }
  //               : chat
  //           )
  //         )
  //       } finally {
  //         setIsUploading(false)
  //       }
  //     }
  //   }
  //   fileReader.readAsDataURL(selectedFile)
  // }

  const startRecording = async () => {
    setIsRecording(true)
    setAudioUrl(null)
    audioChunksRef.current = []

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.start()
    } catch (error) {
      console.error('Error accessing microphone:', error)
      setIsRecording(false)
    }
  }

  const stopAndSendRecording = () => {
    setIsRecording(false)
    setModalTitle('Processing...')

    if (!mediaRecorderRef.current) return

    const mediaRecorder = mediaRecorderRef.current

    mediaRecorder.stop()
    setIsRecording(false) // Set to false immediately when the button is clicked

    mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
      const file = new File([audioBlob], 'user-recording.webm', {
        type: 'audio/webm',
      })
      setAudioFile(file)

      const formData = new FormData()
      formData.append('file', file)
      formData.append('user_id', sessionStorage.getItem('user_id'))
      formData.append('thread_id', sessionStorage.getItem('thread_id'))

      try {
        const response = await fetch(
          sessionStorage.getItem('baseURL') + 'process_audio',
          {
            method: 'POST',
            body: formData,
          }
        )

        console.log('res', response)

        if (response.ok) {
          const processedBlob = await response.blob()
          const processedUrl = URL.createObjectURL(processedBlob)
          setAudioUrl(processedUrl)
          handleShowToast('Audio processed successfully')
          setShowMicModal(false)
        } else {
          handleShowToast('Error processing audio')
        }
      } catch (error) {
        console.error('Error processing audio:', error)
        handleShowToast('Error sending audio')
      }
    }
  }

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.play().catch((error) => {
        console.error('Failed to play audio automatically:', error)
      })
    }
  }, [audioUrl])

  const fixCodeBlocks = (text) => {
    return text.replace(/```\s*``/g, '```') // Replace broken code block indicators
  }

  const cleanMarkdown = (text) => {
    return text
      .replace(/\r\n|\r/g, '\n') // Normalize line endings
      .replace(/([^\n])\n(?=[^\n])/g, '$1 ') // Merge broken lines, preserve paragraphs
      .replace(/(\s*\n){3,}/g, '\n\n') // Remove excessive blank lines
      .replace(/[ ]{2,}/g, ' ') // Collapse multiple spaces
      .replace(/\s+([.,;:!?])/g, '$1') // Remove space before punctuation
      .replace(/([(\[])\s+/g, '$1') // Fix space after opening brackets
      .replace(/\s+([)\]])/g, '$1') // Fix space before closing brackets
      .replace(/\*\*\s*(.*?)\s*\*\*/g, (_, t) => `**${t.trim()}**`) // Normalize bold
      .trim()
  }

  const handleSave = async () => {
    const query = userInput.trim()
    if (!query) return

    setUserInput('')
    setShowChat(false)
    setLoading(false) // No skeleton needed during streaming

    const newChat = { query, response: '' }
    setChatHistory((prev) => [...prev, newChat])
    scrollToBottom()

    const payload = {
      user_message: query,

      thread_id: sessionStorage.getItem('thread_id') || '', // Send empty if none
      username: sessionStorage.getItem('user_id'),
    }

    try {
      const response = await fetch(
        sessionStorage.getItem('baseURL') + 'chat-streaming',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream', // Required for SSE
            Authorization: `Bearer ${sessionStorage.getItem('token')}`,
          },
          body: JSON.stringify(payload),
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} - ${response.statusText}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No readable stream found')

      const decoder = new TextDecoder()
      let done = false
      let accumulatedMarkdown = ''
      let latestRespId = null // ⬅️ Track latest response ID

      while (!done) {
        const { value, done: readerDone } = await reader.read()
        done = readerDone

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk
          .split('\n#\n')
          .filter((line) => line.startsWith('data: '))

        for (const line of lines) {
          const data = line.replace(/^data: /, '')

          // 🧠 Capture latest resp_id
          if (data.startsWith('id=resp_')) {
            latestRespId = data.replace('id=', '').trim()
            continue
          }

          if (data === '') {
            accumulatedMarkdown += '\n\n'
          }
          if (data === '**\n') {
            accumulatedMarkdown += '\n\n\n '
          }

          accumulatedMarkdown += data

          setChatHistory((prev) => {
            const updated = [...prev]
            const lastIndex = updated.length - 1
            updated[lastIndex] = {
              ...updated[lastIndex],
              response: cleanMarkdown(accumulatedMarkdown),
            }
            return updated
          })

          scrollToBottom()
        }
      }

      // ✅ Save thread_id for next query
      if (latestRespId) {
        sessionStorage.setItem('thread_id', latestRespId)
      }
    } catch (error) {
      console.error('Streaming error:', error)
      setChatHistory((prev) =>
        prev.map((chat, index) =>
          index === prev.length - 1
            ? {
              ...chat,
              response: '⚠️ Error retrieving response. Please try again.',
            }
            : chat
        )
      )
      handleShowToast('Error receiving response')
    } finally {
      const textarea = textareaRef.current
      if (textarea) {
        textarea.style.height = 'auto'
      }
    }
  }

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }

  const handleScroll = () => {
    const chatDiv = chatRef.current
    if (chatDiv) {
      const isBottom =
        chatDiv.scrollHeight - chatDiv.scrollTop === chatDiv.clientHeight
      setIsUserScrolledUp(!isBottom)
    }
  }

  const scrollToBottom = () => {
    const chatDiv = chatRef.current
    if (chatDiv) {
      chatDiv.scrollTop = chatDiv.scrollHeight
      setIsUserScrolledUp(false) // Reset the scrolled state
    }
  }

  // Auto-scroll to bottom when new chat messages are added
  useEffect(() => {
    if (chatRef.current && !isUserScrolledUp) {
      scrollToBottom()
    }
  }, [chatHistory, isUserScrolledUp])

  // Effect to close tooltip on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        !event.target.classList.contains('plus-icon')
      ) {
        setShowTooltip(false)
      }
    }

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTooltip])

  useEffect(() => {
    if (chatContentState) {
      setShowChat(true)
      setChatHistory([])
    }
  }, [chatContentState])

  const renderFilePreview = () => {
    if (!file) return null

    if (file.type.startsWith('image/')) {
      return <img src={preview} alt="Preview" className="file-preview-image" />
    } else if (file.type === 'application/pdf') {
      return (
        <div className="file-preview-document">
          <img src={PdfIcon} alt="PDF Preview" width="50" />
          <p>{file.name}</p>
        </div>
      )
    } else if (
      file.type === 'application/msword' ||
      file.type ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return (
        <div className="file-preview-document">
          <img src={DocIcon} alt="Document Preview" width="50" />
          <p>{file.name}</p>
        </div>
      )
    } else {
      return <p className="file-preview-document">File: {file.name}</p>
    }
  }

  // Custom renderer for code blocks, images, videos, etc.
  const renderers = {
    code({ inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '')
      return !inline && match ? (
        <SyntaxHighlighter
          style={dracula}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      )
    },
    h1: ({ children }) => <h1>{children}</h1>,
    h2: ({ children }) => <h2>{children}</h2>,
    h3: ({ children }) => <h3>{children}</h3>,
    h4: ({ children }) => <h4>{children}</h4>,
    h5: ({ children }) => <h5>{children}</h5>,
    h6: ({ children }) => <h6>{children}</h6>,
    p: ({ children }) => <p>{children}</p>,
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    ul: ({ children }) => <ul>{children}</ul>,
    ol: ({ children }) => <ol>{children}</ol>,
    li: ({ children }) => <li>{children}</li>,
    blockquote: ({ children }) => <blockquote>{children}</blockquote>,
    img: ({ src, alt }) => (
      <img src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto' }} />
    ),
    video: ({ src, controls }) => (
      <video src={src} controls style={{ maxWidth: '100%' }}>
        Your browser does not support the video tag.
      </video>
    ),
  }

  return (
    <div className="chat-content-main">
      {isUploading && (
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      )}
      {showMicModal && !isRecording && (
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      )}
      {/* Chat Layout */}
      {showChat ? (
        <div
          className="chat-layout"
          style={{
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          {sessionStorage.getItem('userType') !== 'admin' && (
            <img
              src={imageSrc || FaceIcon}
              alt="GPT icon"
              className="gpt-icon"
            />
          )}
          {sessionStorage.getItem('userType') === 'admin' && (
            <img src={FaceIcon} alt="faceicon" className="gpt-icon" />
          )}
          <div className="d-flex gap-5 cards-layout flex-column flex-lg-row">
            <div className="custom-card">
              <div>
                <img src={DocumentSearch} alt="document-search" width="30" />
              </div>
              <div className="mt-4">
                Type hello and ask for main menu options to start.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="Chats"
          ref={chatRef}
          onScroll={handleScroll}
          style={{ overflowY: 'auto', position: 'relative' }}
        >
          {chatHistory.map((chat, index) => (
            <div key={index} className="chat-block">
              <div className="UserQuery">
                <div className="user-text">{chat.query}</div>
              </div>
              <div className="GptResponse">
                {sessionStorage.getItem('userType') !== 'admin' && (
                  <img
                    src={imageSrc || FaceIcon}
                    alt="GPT icon"
                    className="gpt-icon-chat"
                  />
                )}
                {sessionStorage.getItem('userType') === 'admin' && (
                  <img
                    src={FaceIcon}
                    alt="faceicon"
                    className="gpt-icon-chat"
                  />
                )}

                <div
                  className="ps-2 col-10"
                  style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
                >
                  {loading && index === chatHistory.length - 1 ? (
                    <div style={{ width: '100%' }}>
                      <Skeleton count={3} />
                    </div>
                  ) : (
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={renderers}
                    >
                      {cleanMarkdown(chat.response)}
                    </Markdown>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Input Container */}
      <div className="input-container">
        <div className="input-inner-container">
          {/* File Upload Button */}
          {/* {
            (sessionStorage.getItem('userType') === 'admin') &&
            <span onClick={() => setShowTooltip(!showTooltip)} role="button" className="plus-icon">
              +
            </span>
          } */}

          {/* {showTooltip && (
            <div className="custom-tooltip" ref={tooltipRef}>
              <div className="fw-bold mb-1">Upload file</div>
              <hr />
              <input
                className="col-12 mt-3"
                type="file"
                onChange={handleFileChange}
              />
              {renderFilePreview()}
            </div>
          )} */}

          {/* Text Input Area */}
          <textarea
            ref={textareaRef}
            value={userInput}
            onChange={(event) => {
              setUserInput(event.target.value)
              adjustTextareaHeight()
            }}
            className="InputStyles"
            placeholder="Type your message here..."
            rows={1}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSave()
              }
            }}
            style={{ resize: 'none', overflow: 'hidden' }} // Disable manual resizing
          />

          {/* Click and Hold Mic Icon for Audio Recording */}
          <span
            onClick={() => {
              setShowMicModal(true)
              setModalTitle('Recording...')
            }}
            role="button"
            className="mic-icon"
          >
            <img src={Mic} width="25" alt="mic" />
          </span>
          {audioUrl && (
            <audio ref={audioRef} style={{ display: 'none' }}>
              <source src={audioUrl} type="audio/webm" />
            </audio>
          )}

          {/* Send Button */}
          <span onClick={handleSave} role="button" className="enter-icon">
            <img src={Enter} width="35" alt="enter" />
          </span>

        </div>
      </div>

      <PolicyDialog />

      <div>
        {/* Mic Recording Modal */}
        <Modal
          show={showMicModal}
          onHide={() => setShowMicModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{'Voice Chat'}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <div className="p-5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100"
                height="100"
                fill="currentColor"
                className={`bi bi-mic ${isRecording ? 'mic-recording' : ''}`}
                viewBox="0 0 16 16"
              >
                <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5" />
                <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3" />
              </svg>
            </div>
            {/* Directly call stopAndSendRecording */}
            {/* <Button disabled={!isRecording} variant="success" onClick={stopAndSendRecording} className="mx-3 mt-3">
              Send
            </Button> */}
          </Modal.Body>
        </Modal>

        {/* Start recording when modal opens */}
        {showMicModal && isRecording === false && startRecording()}
      </div>

      {isAudioRoom && !token && (
        <div className="text-center my-3">Fetching voice token...</div>
      )}

      {isAudioRoom && token && (
        <LiveKitRoom
          serverUrl={determineLiveKitServerUrl()} // "wss://tranquilla-c1gx9h6q.livekit.cloud"
          token={token}
          connect={true}
          audio={true}
          video={false}
          onConnected={() => console.log('Connected to LiveKit')}
          style={{ height: 0, width: 0 }}
        >
          <RoomAudioRenderer />
        </LiveKitRoom>
      )}

      {/* Toast Notification */}
      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)}>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  )
}

export default ChatContent
